import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

import API from "@/apiConfig";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paypalOrderId = searchParams.get("paypal_order_id") || searchParams.get("token");
  const type = searchParams.get("type");
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else if (type === "donation" && paypalOrderId) {
      setStatus("success");
    } else {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paypalOrderId, type]);

  const pollStatus = async (id, attempts) => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${id}`);
      if (res.data.payment_status === "paid") {
        setStatus("success");
        setData(res.data);
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(id, attempts + 1), 2000);
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center py-20" data-testid="checkout-success-page">
        <Card className="max-w-md w-full shadow-md">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-gold mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-navy mb-2">Processing Payment...</h2>
                <p className="text-gray-500">Please wait while we confirm your payment.</p>
              </>
            )}
            {status === "success" && (
              <>
                <CheckCircle className="h-14 w-14 text-green-600 mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-navy mb-2">Thank You!</h2>
                <p className="text-gray-600 mb-2">
                  {type === "donation"
                    ? "Your generous donation has been received. God bless you!"
                    : "Your order has been placed successfully."}
                </p>
                {data?.amount_total && (
                  <p className="text-gold font-heading text-xl mb-6">${(data.amount_total / 100).toFixed(2)}</p>
                )}
                <div className="flex gap-3 justify-center">
                  <Link to="/">
                    <Button data-testid="return-home-btn" className="btn-navy">Return Home</Button>
                  </Link>
                  {type !== "donation" && (
                    <Link to="/bookstore">
                      <Button variant="outline" className="border-gold text-navy rounded-sm">Continue Shopping</Button>
                    </Link>
                  )}
                </div>
              </>
            )}
            {(status === "error" || status === "expired" || status === "timeout") && (
              <>
                <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-navy mb-2">
                  {status === "timeout" ? "Payment Verification Timeout" : "Something Went Wrong"}
                </h2>
                <p className="text-gray-600 mb-6">
                  {status === "timeout"
                    ? "Your payment may still be processing. Please check your email for confirmation."
                    : "We couldn't verify your payment. Please contact us at contactus@wordalivetx.com for assistance."}
                </p>
                <Link to="/">
                  <Button data-testid="return-home-error-btn" className="btn-navy">Return Home</Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
